import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import './Toast.scss';

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose, duration = 5000 }) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Time left on the auto-dismiss, so hovering or focusing the toast pauses it
  // (WCAG 2.2.1) and leaving resumes from where it stopped rather than restarting.
  const remainingRef = useRef(duration);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    remainingRef.current = duration;
  }, [duration]);

  useEffect(() => {
    if (paused) return undefined;
    const startedAt = Date.now();
    const timer = setTimeout(() => onCloseRef.current(), remainingRef.current);
    return () => {
      clearTimeout(timer);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAt));
    };
  }, [paused, duration]);

  // Errors interrupt (alert); confirmations and info wait for a pause (status).
  const isError = type === 'error';

  return (
    <div
      className={`toast toast-${type}`}
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      aria-atomic="true"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setPaused(false); }}
    >
      <div className="toast-content">
        <span className="toast-message">{message}</span>
      </div>
      <button type="button" className="toast-close" onClick={onClose} aria-label="Dismiss notification">
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
};

export default Toast;
